import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import { getSiteBrandName, setPageTitle } from '../Utils/brand';
import { ScaleIcon, WarningIcon, ArrowLeftIcon } from '../Components/Icons';

export default function TermsOfServicePage() {
    const brandName = getSiteBrandName();

    useEffect(() => {
        setPageTitle('Terms of Service');
    }, []);

    return (
        <main className="pt-24 min-h-screen">
            <section className="py-12 px-6 md:px-8 lg:px-16">
                <div className="max-w-4xl mx-auto space-y-10">
                    {/* Header */}
                    <div className="text-center space-y-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300 text-xs font-medium tracking-wide">
                            <ScaleIcon className="w-3.5 h-3.5 text-sky-400" />
                            <span>Legal Terms & User Agreement</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-display font-extrabold text-white tracking-tight">
                            Terms of <span className="bg-gradient-to-r from-sky-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">Service</span>
                        </h1>
                        <p className="text-sm md:text-base text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                            Last updated: October 2026. Please review these Terms of Service carefully before purchasing, renting, or configuring URL redirection for domains on {brandName}.
                        </p>
                    </div>

                    {/* Content Card */}
                    <div className="p-8 md:p-12 space-y-10 text-neutral-300 leading-relaxed border border-white/10 bg-neutral-900/60 backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_15px_35px_rgba(0,0,0,0.5)]">
                        
                        {/* Section 1 */}
                        <div className="space-y-3">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">1</span>
                                Acceptance of Terms
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                By accessing our website, initiating a transaction, or utilizing our domain services (purchase, rental, or URL redirection), you agree to be bound by these Terms of Service and all applicable regulations.
                            </p>
                        </div>

                        {/* Section 2 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">2</span>
                                Domain Services & Rights
                            </h2>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li><strong className="text-neutral-200">Full Purchase:</strong> Grants complete legal title and ownership transfer of the domain name. The buyer receives the EPP/Auth code to transfer the domain to any accredited registrar.</li>
                                <li><strong className="text-neutral-200">Domain Rental:</strong> Grants exclusive DNS authority to point root A-records and www CNAME records to the lessee's designated server IP for the paid duration.</li>
                                <li><strong className="text-neutral-200">Domain Forwarding:</strong> Configures automated HTTP 301/302 redirects from the domain to a specified destination URL for the duration of the contracted period.</li>
                            </ul>
                        </div>

                        {/* Section 3 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">3</span>
                                Payment, Settlement & Refund Guarantee
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                We operate an automated atomic settlement protocol:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li><strong className="text-neutral-200">First-Confirmed Claim:</strong> The domain is allocated instantly to the first transaction confirmed on the blockchain or via payment gateways.</li>
                                <li><strong className="text-neutral-200">Compromise Protection:</strong> In the rare event of simultaneous payments where an earlier confirmation claims the domain, your funds are 100% protected with an immediate refund or booking credit.</li>
                                <li><strong className="text-neutral-200">Finality of Activated Rentals:</strong> Once DNS records are successfully propagated and active, payments for that elapsed duration are non-refundable.</li>
                            </ul>
                        </div>

                        {/* Section 4 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">4</span>
                                Acceptable Use Policy (AUP)
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                You agree NOT to use any domain rented or forwarded through our service for illegal, malicious, or abusive activities, including:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li>Phishing, credential harvesting, or deceptive financial impersonation.</li>
                                <li>Distribution of malware, ransomware, botnet command-and-control, or spyware.</li>
                                <li>Terrorist propaganda, child exploitation, or violent extremism.</li>
                                <li>Unsolicited bulk email (SPAM) distribution or DNS amplification attacks.</li>
                            </ul>
                            <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs leading-relaxed flex items-start gap-2.5 mt-2">
                                <WarningIcon className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                                <div>
                                    <strong className="font-semibold text-rose-200">Strict Enforcement:</strong> Any violation of our AUP will result in immediate termination of DNS routing without prior notice and without refund.
                                </div>
                            </div>
                        </div>

                        {/* Section 5 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">5</span>
                                Technical Disclaimers & DNS Propagation
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                While our internal DNS updates occur within seconds, global DNS propagation is governed by third-party Internet Service Providers and TTL caches worldwide. We do not guarantee zero-downtime during external registrar maintenance or upstream carrier outages.
                            </p>
                        </div>

                        {/* Section 6 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">6</span>
                                Expiration & Automated Reversion
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                Upon expiration of your rental or forwarding agreement, the domain's DNS routing will automatically revert to our marketplace default parking state unless renewed prior to the expiration timestamp.
                            </p>
                        </div>

                        {/* Section 7 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">7</span>
                                Limitation of Liability
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                {brandName} and its operators shall not be liable for any indirect, incidental, special, or consequential damages resulting from the use or inability to use our services. In no event shall our total liability exceed the total amount paid by you for the specific transaction in dispute.
                            </p>
                        </div>

                        {/* Section 8 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">8</span>
                                Contact & Inquiries
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                For inquiries or resolution regarding these Terms, please contact our administrative desk through our{' '}
                                <Link to="/contact.html" className="text-sky-400 hover:text-sky-300 underline font-medium">Contact Page</Link>{' '}
                                or reach out to our official Telegram support.
                            </p>
                        </div>
                    </div>

                    {/* Back link */}
                    <div className="text-center pt-2">
                        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-neutral-300 hover:text-white text-xs sm:text-sm font-medium transition-all">
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Return to Marketplace</span>
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
